import React from 'react';
import './MedicalQuestionnaire.css';

const MedicalQuestionnaire = ({ questionnaire, onChange }) => {
  // Handle changes to questionnaire responses
  const handleQuestionChange = (section, question, field, value) => {
    const updatedQuestionnaire = {
      ...questionnaire,
      [section]: {
        ...questionnaire[section],
        [question]: {
          ...questionnaire[section][question],
          [field]: value
        }
      }
    };
    onChange(updatedQuestionnaire);
  };

  // Handle changes to current padecimiento
  const handleCurrentChange = (value) => {
    const updatedQuestionnaire = {
      ...questionnaire,
      current: {
        ...questionnaire.current,
        padecimientoActual: value
      }
    };
    onChange(updatedQuestionnaire);
  };

  // Render a yes/no question with optional details field
  const renderQuestion = (section, questionKey, label, showDetails = true, showMonths = false) => {
    const question = questionnaire[section]?.[questionKey] || { answer: null, details: '', meses: '' };
    
    return (
      <div className="questionnaire-item" key={questionKey}>
        <div className="questionnaire-row">
          <div className="question-label">{label}</div>
          <div className="yes-no-options">
            <label className="radio-option">
              <input
                type="radio"
                name={`${section}_${questionKey}`}
                checked={question.answer === true}
                onChange={() => handleQuestionChange(section, questionKey, 'answer', true)}
              />
              <span>Sí</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name={`${section}_${questionKey}`}
                checked={question.answer === false}
                onChange={() => handleQuestionChange(section, questionKey, 'answer', false)}
              />
              <span>No</span>
            </label>
          </div>
          {showMonths && (
            <div className="months-input">
              <span>Meses:</span>
              <input
                type="text"
                value={question.meses || ''}
                onChange={(e) => handleQuestionChange(section, questionKey, 'meses', e.target.value)}
                placeholder="Meses"
                className="months-field"
              />
            </div>
          )}
        </div>
        {showDetails && question.answer === true && (
          <div className="details-row">
            <input
              type="text"
              value={question.details || ''}
              onChange={(e) => handleQuestionChange(section, questionKey, 'details', e.target.value)}
              placeholder="Detalles"
              className="details-input"
            />
          </div>
        )}
      </div>
    );
  };

  // Render hereditary family history question
  const renderFamilyHistoryQuestion = (questionKey, label) => {
    const question = questionnaire.familyHistory?.[questionKey] || { answer: null, details: '' };
    
    return (
      <div className="questionnaire-item" key={questionKey}>
        <div className="questionnaire-row">
          <div className="question-label">{label}</div>
          <div className="yes-no-options">
            <label className="radio-option">
              <input
                type="radio"
                name={`familyHistory_${questionKey}`}
                checked={question.answer === true}
                onChange={() => handleQuestionChange('familyHistory', questionKey, 'answer', true)}
              />
              <span>Sí</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name={`familyHistory_${questionKey}`}
                checked={question.answer === false}
                onChange={() => handleQuestionChange('familyHistory', questionKey, 'answer', false)}
              />
              <span>No</span>
            </label>
          </div>
        </div>
        {question.answer === true && (
          <div className="details-row">
            <input
              type="text"
              value={question.details || ''}
              onChange={(e) => handleQuestionChange('familyHistory', questionKey, 'details', e.target.value)}
              placeholder="Detalles"
              className="details-input"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="medical-questionnaire">
      {/* ANTECEDENTES HEREDOFAMILIARES - First Section */}
      <h3 className="questionnaire-title">ANTECEDENTES HEREDOFAMILIARES</h3>
      
      <div className="family-history-section">
        <div className="questionnaire-grid">
          <div className="questionnaire-column">
            {renderFamilyHistoryQuestion('padre', 'Padre')}
            {renderFamilyHistoryQuestion('abuelos_paternos', 'Abuelos paternos')}
          </div>
          <div className="questionnaire-column">
            {renderFamilyHistoryQuestion('madre', 'Madre')}
            {renderFamilyHistoryQuestion('abuelos_maternos', 'Abuelos maternos')}
          </div>
        </div>
      </div>

      <h3 className="questionnaire-title">ANTECEDENTES PATOLÓGICOS Y NO PATOLÓGICOS</h3>
      
      <div className="questionnaire-grid">
        {/* Left column - Pathological conditions */}
        <div className="questionnaire-column">
          {renderQuestion('pathological', 'hipertension', 'Hipertensión')}
          {renderQuestion('pathological', 'enfermedadesCorazon', 'Enfermedades del corazón')}
          {renderQuestion('pathological', 'diabetes', 'Diabetes')}
          {renderQuestion('pathological', 'vih', 'VIH')}
          {renderQuestion('pathological', 'artritis', 'Artritis')}
          {renderQuestion('pathological', 'convulsiones', 'Convulsiones')}
          {renderQuestion('pathological', 'infartosMiocardio', 'Infartos al miocardio')}
          {renderQuestion('pathological', 'alergiaAntibioticos', 'Alergia a medicamentos')}
          {renderQuestion('pathological', 'alergiaAnestesicos', 'Alergia a anestésicos')}
        </div>
        
        {/* Right column - Other conditions */}
        <div className="questionnaire-column">
          {renderQuestion('pathological', 'discrepanciasSanguineas', 'Discrepancias sanguíneas')}
          {renderQuestion('pathological', 'enfViasRespiratorias', 'Enf. vías respiratorias')}
          {renderQuestion('pathological', 'hepatitis', 'Hepatitis')}
          {renderQuestion('pathological', 'epilepsia', 'Epilepsia')}
          {renderQuestion('lifestyle', 'embarazo', 'Embarazo', false, true)}
          {renderQuestion('lifestyle', 'cancer', 'Cáncer')}
          {renderQuestion('lifestyle', 'bifosfonatos', '¿Está tomando bifosfonatos?')}
          {renderQuestion('lifestyle', 'cirugiasPrevias', '¿Cirugías previas?')}
          {renderQuestion('lifestyle', 'otraEnfermedad', '¿Tiene alguna enfermedad que no se haya mencionado?')}
        </div>
      </div>

      {/* Lifestyle questions */}
      <div className="lifestyle-section">
        {renderQuestion('lifestyle', 'bajoTratamientoMedico', '¿Está bajo tratamiento médico?')}
        {renderQuestion('lifestyle', 'fuma', '¿Fuma?', true)}
        {renderQuestion('lifestyle', 'consumeAlcohol', '¿Consume bebidas alcohólicas?', true)}
        {renderQuestion('lifestyle', 'consumeSustanciasPsicoactivas', '¿Consume sustancias psicoactivas?', true)}
        {renderQuestion('lifestyle', 'enjuagueBucal', '¿Utiliza enjuague bucal?', true)}
        {renderQuestion('lifestyle', 'hiloDental', '¿Utiliza hilo dental?', false)}
      </div>

      {/* Last dental visit */}
      <div className="current-condition">
        <div className="current-label">Fecha y motivo de la última consulta odontológica</div>
        <textarea
          value={questionnaire.current?.ultimaConsultaOdontologica || ''}
          onChange={(e) => {
            const updatedQuestionnaire = {
              ...questionnaire,
              current: {
                ...questionnaire.current,
                ultimaConsultaOdontologica: e.target.value
              }
            };
            onChange(updatedQuestionnaire);
          }}
          placeholder="Describa la fecha y motivo de la última consulta odontológica..."
          className="current-textarea"
          rows={2}
        />
      </div>

      {/* Current condition */}
      <div className="current-condition">
        <div className="current-label">Padecimiento actual (motivo de consulta)</div>
        <textarea
          value={questionnaire.current?.padecimientoActual || ''}
          onChange={(e) => handleCurrentChange(e.target.value)}
          placeholder="Describa el padecimiento actual..."
          className="current-textarea"
          rows={3}
        />
      </div>
    </div>
  );
};

export default MedicalQuestionnaire;
